import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuditorTask } from '../models/AuditorTask';
import { environment } from 'src/environments/environment';
import { TaskCameraDto } from '../dtos/TaskCameraDto';
import { AuditToReview } from '../models/AuditToReview';
import { LatencyStat } from '../models/LatencyStat';
import { ImageAuditsDto, CreateReportImage, SaveAudit, ReportImage } from '../models/ImageAuditsDto';
import { TrainingImage } from '../models/TrainingImage';
import { GroupedAuditorMistakes } from '../models/GroupedAuditorMistakes';
import { IAPIResponse } from '../../shared/models/IApiResponse';
import { AuditorMistakeResponse } from '../models/AuditedMistakeImage';
import { CompletedTask } from '../models/CompletedTask';
import { AuditorInfo } from '../models/AuditorInfo';
import { AuditorMetrics } from '../../shared/models/AuditorMetrics';
import { Reward } from '../models/Reward';
import { AuditorPerformance } from '../models/AuditorPerformance';

export interface GetMistakesFilters {
  customerId?: number;
  locationId?: number;
  areaId?: number;
  sectionId?: number;
  cameraId?: number;
  page?: number;
  pageSize?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuditingService {
  constructor(private http: HttpClient) {}

  getTasks() {
    return this.http.get<AuditorTask[]>(`${environment.apiBase}/v1.0/auditing/tasks`);
  }

  getAvailableTasks() {
    return this.http.get<AuditorTask[]>(`${environment.apiBase}/v1.0/tasks/available`);
  }

  getTask(taskId: number) {
    return this.http.get<AuditorTask>(`${environment.apiBase}/v1.0/auditing/tasks/${taskId}`);
  }

  getCameraImages(taskId: number) {
    return this.http.get<TaskCameraDto[]>(`${environment.apiBase}/v1.0/auditing/task/images/${taskId}`);
  }

  getTemplateButtons(cameraId: number, at: number) {
    return this.http.get<ImageAuditsDto>(`${environment.apiBase}/v1.0/auditing/image/categories/${cameraId}/${at}`);
  }

  saveImageAudits({ taskProgress, items, cameraId, at, position }: SaveAudit) {
    if (taskProgress && (taskProgress.progress < 0 || taskProgress.progress > 100)) {
      throw new Error(`Invalid progress`);
    }
    return this.http.post<any>(`${environment.apiBase}/v1.0/auditing/image/categories/${cameraId}/${at}`, {
      items,
      taskProgress,
      position
    });
  }

  getImageReports(cameraId: number, at: number) {
    return this.http.get<ReportImage[]>(`${environment.apiBase}/v1.0/Auditing/report/${cameraId}/${at}`);
  }

  reportImage(report: CreateReportImage) {
    return this.http.post<ReportImage>(`${environment.apiBase}/v1.0/Auditing/report/${report.cameraId}/${report.periodId}`, report);
  }

  startTask(taskId: number) {
    return this.http.post<AuditorTask>(`${environment.apiBase}/v1.0/auditing/task/start/${taskId}`, null);
  }

  confirmTask(taskId: number, comment: string) {
    return this.http.post<void>(`${environment.apiBase}/v1.0/auditing/task/confirm/${taskId}`, comment, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  getAuditingSummary(taskId: number) {
    return this.http.get<{ auditedImages: AuditToReview[] }>(`${environment.apiBase}/v1.0/auditing/summary/${taskId}`);
  }

  reportImageLatencyStats(taskId: number, stats: LatencyStat[]) {
    console.log(`Reporting latency window: [${stats.map(s => s.latency).join(',')}]`);
    return this.http.post<void>(`${environment.apiBase}/v1.0/auditing/task/loadstats/${taskId}`, stats);
  }

  getNotificationState(): Observable<AuditorInfo> {
    return this.http.get<AuditorInfo>(`${environment.apiBase}/v1.0/auditing/about`);
  }

  setNotificationState(notify: boolean): Observable<void> {
    return this.http.post<void>(`${environment.apiBase}/v1.0/auditing/${notify ? 'notify' : 'nonotify'}`, null);
  }

  setAuditorAvailabilityState(isAuditorReady: boolean): Observable<void> {
    return this.http.post<void>(`${environment.apiBase}/v1.0/auditing/ready`, JSON.stringify(isAuditorReady), {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  takeTask(taskId: number): Observable<AuditorTask> {
    return this.http.post<AuditorTask>(`${environment.apiBase}/v1.0/tasks/take/${taskId}`, null);
  }

  takeAllTasks(branchId: number, periodId: string, taskIds: number[]): Observable<AuditorTask[]> {
    return this.http.post<AuditorTask[]>(`${environment.apiBase}/v1.0/tasks/take-all`, {
      branchId,
      periodId,
      taskIds
    });
  }

  giveUpTask(taskId: number, comment: string): Observable<void> {
    let params;
    if (comment) params = new HttpParams().set('reason', comment);
    return this.http.post<void>(`${environment.apiBase}/v1.0/tasks/giveup/${taskId}`, null, { params });
  }

  submitRequest(taskId: number, message: string, imageUrl: string, taskUrl: string) {
    return this.http.post<string>(
      `${environment.apiBase}/v1.0/tasks/support/${taskId}`,
      {
        message,
        imageUrl,
        taskUrl,
      },
      { responseType: 'text' as 'json' },
    );
  }

  loadImageGallery(cameraId: number) {
    return this.http.get<TrainingImage[]>(`${environment.apiBase}/v1.0/tasks/trainingImages/${cameraId}`);
  }

  getMistakes(filters: GetMistakesFilters): Observable<IAPIResponse<GroupedAuditorMistakes[]>> {
    const baseParams: Record<string, any> = {};

    const data = this.http.get<IAPIResponse<GroupedAuditorMistakes[]>>(`${environment.apiBase}/v1.0/auditingevents/grouped-mistakes`, {
      params: Object.assign(baseParams, ...Object.keys(filters).map((k: keyof GetMistakesFilters) => ({ [k]: String(filters[k]) }))),
    });

    return data;
  }

  getReviewMistakesForTask(taskId: number): Observable<AuditorMistakeResponse> {
    return this.http.get<AuditorMistakeResponse>(`${environment.apiBase}/v1.0/auditingevents/review-mistakes/${taskId}`);
  }

  acknowledgeMistakes(taskId: number): Observable<void> {
    return this.http.put<void>(`${environment.apiBase}/v1.0/auditingevents/review-mistakes/${taskId}/acknowledge`, {});
  }

  trackUnseenFlaggedImages(
    taskId: number,
    stats: {
      unseenFlaggedImagesCount: number;
      seenFlaggedImagesCount: number;
      averageTimePerFlaggedImage: number;
    },
  ) {
    return this.http.post<void>(`${environment.apiBase}/v1.0/auditing/track/${taskId}/unseen`, stats);
  }

  loadCompletedTasks(from: string, to: string, paid?: boolean) {
    let params = new HttpParams();
    if (paid !== null) params = params.set('paid', paid);

    return this.http.get<{ tasks: CompletedTask[]; total: number; totalSum?: number }>(
      `${environment.apiBase}/v1.0/auditing/tasks/completed/${from}/${to}`,
      {
        params,
      },
    );
  }

  loadUserMetrics(): Observable<AuditorMetrics> {
    return this.http.get<AuditorMetrics>(`${environment.apiBase}/v1.0/auditing/metrics/`);
  }

  loadPerformance(month: string): Observable<AuditorPerformance> {
    return this.http.get<AuditorPerformance>(`${environment.apiBase}/v1.0/auditing/Performance/${month}`);
  }

  loadUserBadges(): Observable<Reward[]> {
    return this.http.get<Reward[]>(`${environment.apiBase}/v1.0/auditing/badges/`);
  }
}
